import type { ReactNode } from "react";
import type { SxProps } from "@mui/joy/styles/types";
import { Modal, ModalDialog, Box, CircularProgress } from "@mui/joy";
import { CloseRoundedIcon, CheckRoundedIcon } from "../../styles/StyledMUI";
import DarkTypography from "./DarkTypography";
import IconButtonCTA from "./IconButtonCTA";
import {
  type CommonModalSize,
  commonModalBackdropSx,
  commonModalBodySx,
  commonModalCloseButtonSx,
  commonModalConfirmIconButtonSx,
  commonModalDialogBaseSx,
  commonModalDialogRadiusSx,
  commonModalDialogSizeSx,
  commonModalFooterActionsFlexSx,
  commonModalFooterRowSx,
  commonModalFooterSaveWrapSx,
  commonModalHeaderRowSx,
  commonModalSubtitleTypographySx,
  commonModalTitleTypographySx,
  commonModalTitleWrapSx,
} from "../../styles/globalStyles";

export type { CommonModalSize };

export interface CommonModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional line under the title (e.g. date + context). */
  subtitle?: ReactNode;
  children: ReactNode;
  onConfirm?: () => void | Promise<void>;
  confirmLabel?: string;
  /** Icon for the default confirm CTA (defaults to check). */
  confirmIcon?: ReactNode;
  submitDisabled?: boolean;
  loading?: boolean;
  size?: CommonModalSize;
  disableEnforceFocus?: boolean;
  footerActions?: ReactNode;
  /** Dialog height follows content; capped by size maxHeight. Content scrolls if needed. */
  fitContentHeight?: boolean;
  /** Extra ModalDialog sx (merged after size presets). */
  dialogSx?: SxProps;
  /** Blocking mode: hides the close button and ignores Escape / backdrop clicks. */
  closeDisabled?: boolean;
}

export default function CommonModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  onConfirm,
  confirmLabel = "Enregistrer",
  confirmIcon,
  submitDisabled = false,
  loading = false,
  size = "lg",
  disableEnforceFocus = false,
  footerActions,
  fitContentHeight = false,
  dialogSx,
  closeDisabled = false,
}: CommonModalProps) {
  const showDefaultSave = Boolean(onConfirm);
  const hasFooter = showDefaultSave || Boolean(footerActions);
  const modalDialogSize = size === "xl" ? "lg" : size;

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!closeDisabled) onClose();
      }}
      disableEscapeKeyDown={closeDisabled}
      disableEnforceFocus={disableEnforceFocus}
      sx={commonModalBackdropSx}
    >
      <ModalDialog
        size={modalDialogSize}
        layout="center"
        sx={
          [
            commonModalDialogBaseSx,
            commonModalDialogSizeSx(size, fitContentHeight),
            commonModalDialogRadiusSx,
            dialogSx,
          ] as SxProps
        }
      >
        <Box sx={commonModalHeaderRowSx}>
          <Box sx={commonModalTitleWrapSx}>
            <DarkTypography level="h3" colorVariant="title" sx={commonModalTitleTypographySx}>
              {title}
            </DarkTypography>
            {subtitle != null && subtitle !== "" && (
              <DarkTypography
                level="body-sm"
                colorVariant="muted"
                component="div"
                sx={commonModalSubtitleTypographySx}
              >
                {subtitle}
              </DarkTypography>
            )}
          </Box>
          {!closeDisabled && (
            <IconButtonCTA
              variant="plain"
              color="neutral"
              onClick={onClose}
              size="sm"
              aria-label="Fermer"
              sx={commonModalCloseButtonSx}
            >
              <CloseRoundedIcon />
            </IconButtonCTA>
          )}
        </Box>

        <Box sx={commonModalBodySx(fitContentHeight)}>{children}</Box>

        {hasFooter && (
          <Box sx={commonModalFooterRowSx(showDefaultSave)}>
            {footerActions ? (
              <Box sx={commonModalFooterActionsFlexSx}>{footerActions}</Box>
            ) : showDefaultSave ? (
              <Box />
            ) : null}
            {showDefaultSave ? (
              <Box sx={commonModalFooterSaveWrapSx}>
                <IconButtonCTA
                  ctaVariant="accent"
                  disabled={submitDisabled || loading}
                  size="sm"
                  onClick={onConfirm}
                  aria-label={confirmLabel}
                  sx={commonModalConfirmIconButtonSx}
                >
                  {loading ? <CircularProgress size="sm" /> : (confirmIcon ?? <CheckRoundedIcon />)}
                </IconButtonCTA>
              </Box>
            ) : null}
          </Box>
        )}
      </ModalDialog>
    </Modal>
  );
}
